import Box from "@mui/joy/Box";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useState } from "react";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { taskApi, TaskType, TASK_DOMAIN, TASK_LIST_ENDPOINT } from "api";
import {
  Button,
  CircularProgress,
  ListDivider,
  Stack,
  SvgIcon,
} from "@mui/joy";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { useCallback } from "react";
import { menuSelector } from "recoil/menuAtom";
import { taskListAtom } from "recoil/taskListAtom";
import useOpenTask from "hooks/useOpenTask";
import { userAtom } from "recoil/userAtom";
import { SidebarMenuType } from "components/sidebar/SideBar";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import useIsInappWebview from "hooks/useIsInappWebview";
import { isMobile } from "react-device-detect";
import { deepLinkData } from "data/deepLinkData";
import TaskItem from "./TaskItem";
import { useTranslation } from "react-i18next";
import BackToPatientListButton from "./BackToPatientListButton";
import CoachMenu from "./CoachMenu";
import AppInstallModal from "./AppInstallModal";
import { isDev } from "config";

type menuKeyType = "taskList_ongoing" | "taskList_todo" | "taskList_completed";

function NestedList({
  menuKey,
  title,
  taskList,
  initiallyOpen,
}: {
  menuKey: menuKeyType;
  title: string;
  taskList: TaskType[];
  initiallyOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useRecoilState(menuSelector(menuKey));

  useEffect(() => {
    if (initiallyOpen) {
      setIsOpen(true);
    }
  }, [initiallyOpen]);

  return (
    <ListItem nested sx={{ py: "14px" }}>
      <ListItemButton
        onClick={() => {
          setIsOpen((isOpen) => !isOpen);
        }}
        sx={{ py: 0 }}
      >
        <ListItemContent>
          <Typography level={"body-xs"}>
            {title}
            <Typography level={"body-xs"} sx={{ opacity: 0.5, pl: 1 }}>
              {taskList.length}
            </Typography>
          </Typography>
        </ListItemContent>
        <SvgIcon sx={{ color: "primary.solid" }}>
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </SvgIcon>
      </ListItemButton>
      {isOpen && (
        <List
          sx={{
            "--List-gap": "0px",
          }}
        >
          {taskList?.map(({ taskKey, isDone, isLocked, hasMissingValue }) => (
            <TaskItem
              key={taskKey}
              taskKey={taskKey}
              isDone={isDone}
              isLocked={isLocked}
              hasMissingValue={hasMissingValue}
            />
          ))}
        </List>
      )}
    </ListItem>
  );
}

export default function TaskList({
  setCurrentMenu,
}: {
  setCurrentMenu: React.Dispatch<React.SetStateAction<SidebarMenuType>>;
}) {
  const { isNotStarted } = useSubscriptionStatus();
  const isInappWebview = useIsInappWebview();

  const { t: t_ui } = useTranslation("translation", {
    keyPrefix: "ui",
  });

  const [taskListState, setTaskListState] = useRecoilState(taskListAtom);

  const patientState = useRecoilValue(patientAtom);
  const userState = useRecoilValue(userAtom);
  const isCoach = userState?.accessToken && userState?.role !== "patient";

  const [listData, setListData] = useState<
    | {
        listTitle: string;
        taskList: TaskType[];
        menuKey: menuKeyType;
        initiallyOpen?: boolean;
      }[]
    | undefined
  >();
  const [showGoMarketQr, setShowGoMarkeyQr] = useState(false);

  const queryFn = useCallback(
    () => taskApi.getList(`${patientState?.patientId}`),
    [patientState]
  );

  const [loading, setLoading] = useState(false);
  const [isFirstTaskOpen, setIsFirstTaskOpen] = useState<boolean>(false);

  const { isFetching } = useQuery(
    [TASK_DOMAIN, TASK_LIST_ENDPOINT, patientState?.patientId],
    queryFn,
    {
      enabled: !!userState?.accessToken && !!patientState?.patientId,
      // refetchOnWindowFocus: false,
      refetchInterval: 10000,
      onSuccess: (data) => {
        if (data && data.result) {
          const list = data.result;
          setTaskListState(
            !isCoach && isNotStarted
              ? list.map((each) => ({
                  ...each,
                  isLocked: each.taskId === "0-0-A" ? false : true,
                }))
              : list
          );

          const onboardingActivity = list.find(
            (element) => element.taskId === "0-0-A"
          );
          //0-0-A 안마친 경우
          if (
            list.length === 1 &&
            onboardingActivity &&
            !onboardingActivity.isDone
          ) {
            if (!isCoach) {
              if (isInappWebview) {
                if (!isFirstTaskOpen) {
                  openOnboardingTask();
                  setIsFirstTaskOpen(true);
                }
              } else {
                if (isMobile) {
                  window.location.href = deepLinkData.install.url;
                } else {
                  if (!isDev) {
                    setShowGoMarkeyQr(true);
                  }
                }
              }
            }
          }
        }
      },
    }
  );
  useEffect(() => {
    // patientId가 변경되었을 때 로딩 상태를 true로 설정
    if (patientState?.patientId) {
      setLoading(true);
    }
  }, [patientState?.patientId]);

  useEffect(() => {
    // 데이터가 fetching 중이 아니면 로딩 상태를 false로 설정
    if (!isFetching) {
      setLoading(false);
    }
  }, [isFetching]);

  const openOnboardingTask = useOpenTask(
    `0-0-A:${patientState?.patientId}`,
    true
  );

  useEffect(() => {
    if (!userState?.accessToken && !patientState?.patientId) {
      setTaskListState([
        {
          taskKey: `0-0-A:${patientState?.patientId}`,
          taskId: "0-0-A",
          isDone: false,
          isOpen: true,
          hasMissingValue: false,
        },
      ]);

      openOnboardingTask();
    }
  }, [patientState, userState?.accessToken]);

  useEffect(() => {
    if (taskListState) {
      const todoList = taskListState.filter(
        (element) => !element.taskId.includes("T") && element.isOpen
      );
      const ongoingList = taskListState.filter((element) =>
        element.taskId.includes("T")
      );
      const doneList = taskListState.filter((element) => !element.isOpen);
      setListData([
        {
          listTitle: "다음 할 활동",
          taskList: todoList,
          menuKey: "taskList_todo" as menuKeyType,
          initiallyOpen: true,
        },
        ...(ongoingList.length > 0
          ? [
              {
                listTitle: "꾸준히 하는 활동",
                taskList: ongoingList,
                menuKey: "taskList_ongoing" as menuKeyType,
                initiallyOpen: true,
              },
            ]
          : []),
        ...(doneList.length > 0
          ? [
              {
                listTitle: "마친 활동",
                taskList: doneList,
                menuKey: "taskList_completed" as menuKeyType,
              },
            ]
          : []),
      ]);
    }
  }, [taskListState]);

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        ...(!listData && { height: "100%" }),
      }}
    >
      {showGoMarketQr && <AppInstallModal />}
      {isCoach && patientState && (
        <BackToPatientListButton setCurrentMenu={setCurrentMenu} />
      )}

      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
        }}
      >
        <List
          sx={{
            pt: 0,
            "--ListItem-radius": "8px",
            display: "flex",
          }}
        >
          {isCoach && patientState && <CoachMenu />}
          <Stack
            justifyContent="center"
            sx={{
              pt: "14px",
              pb: "14px",
              borderBottom: "1px solid",
              borderColor: "background.level3",
            }}
          >
            <TaskItem taskKey={"thoughtmap"} />
            <TaskItem taskKey={"progress"} />
          </Stack>
          {!loading && listData ? (
            listData.map(
              ({ listTitle, taskList, menuKey, initiallyOpen }, index) => (
                <Box key={listTitle}>
                  <NestedList
                    menuKey={menuKey}
                    title={t_ui(listTitle)}
                    taskList={[...taskList]}
                    initiallyOpen={initiallyOpen}
                  />
                  {index < listData.length - 1 && (
                    <ListDivider
                      sx={{
                        backgroundColor: "background.level3",
                      }}
                    />
                  )}
                </Box>
              )
            )
          ) : (
            <Stack
              justifyContent={"center"}
              alignItems="center"
              sx={{
                flexGrow: 1,
              }}
            >
              <CircularProgress
                sx={{
                  mt: "14px",
                  mx: "auto",
                }}
                color="neutral"
              />
            </Stack>
          )}
        </List>
      </Box>
    </Sheet>
  );
}
